/**
 * Global state management for Elevar event deduplication
 * Prevents duplicate events from firing during page refresh/hydration
 */

type EventKey = `${string}::${string}`; // format: "eventType::pathname"

interface EventTrackingState {
  // Track events that have fired during this browser session
  firedEvents: Set<EventKey>;
  // Track event + timestamp for rapid-fire protection
  eventTimestamps: Map<EventKey, number>;
  // Track if initial page events have fired (reset on navigation)
  initialPageEventsFired: boolean;
  // Current pathname to detect navigation
  currentPathname: string;
}

// Global state to prevent duplicate events across component remounts
const globalEventState: EventTrackingState = {
  firedEvents: new Set<EventKey>(),
  eventTimestamps: new Map<EventKey, number>(),
  initialPageEventsFired: false,
  currentPathname: '',
};

/**
 * Creates a unique key for an event based on type and pathname
 */
function createEventKey(eventType: string, pathname: string): EventKey {
  return `${eventType}::${pathname}`;
}

/**
 * Checks if an event should be allowed to fire
 * Returns true if the event should fire, false if it should be skipped
 */
export function shouldFireEvent(
  eventType: string,
  pathname: string,
  options: {
    allowRefireAfterMs?: number; // Allow refiring after this many milliseconds
    isInitialPageLoad?: boolean; // Is this an initial page load event?
  } = {}
): boolean {
  const { allowRefireAfterMs = 100, isInitialPageLoad = false } = options;
  const eventKey = createEventKey(eventType, pathname);

  // Handle navigation: clear state when pathname changes
  if (
    globalEventState.currentPathname &&
    globalEventState.currentPathname !== pathname
  ) {
    // Clear events from other pages but keep current page events
    for (const key of globalEventState.firedEvents) {
      if (
        !key.endsWith(`::${globalEventState.currentPathname}`) &&
        !key.endsWith(`::${pathname}`)
      ) {
        globalEventState.firedEvents.delete(key);
        globalEventState.eventTimestamps.delete(key);
      }
    }
    globalEventState.initialPageEventsFired = false;
    globalEventState.currentPathname = pathname;
  }

  // Set current pathname if not set
  if (!globalEventState.currentPathname) {
    globalEventState.currentPathname = pathname;
  }

  // Check if this is an initial page load event that has already fired
  if (isInitialPageLoad && globalEventState.initialPageEventsFired) {
    return false;
  }

  // Check if this exact event has already fired for this pathname
  if (globalEventState.firedEvents.has(eventKey)) {
    // Check timestamp for rapid-fire protection
    const lastFired = globalEventState.eventTimestamps.get(eventKey);
    const now = Date.now();

    if (lastFired && now - lastFired < allowRefireAfterMs) {
      return false; // Too soon, skip this event
    }
  }

  return true; // Event is allowed to fire
}

/**
 * Marks an event as fired to prevent duplicates
 */
export function markEventAsFired(
  eventType: string,
  pathname: string,
  options: {
    isInitialPageLoad?: boolean;
  } = {}
): void {
  const { isInitialPageLoad = false } = options;
  const eventKey = createEventKey(eventType, pathname);
  const now = Date.now();

  globalEventState.firedEvents.add(eventKey);
  globalEventState.eventTimestamps.set(eventKey, now);

  if (isInitialPageLoad) {
    globalEventState.initialPageEventsFired = true;
  }
}

/**
 * Clears fired events for a specific pathname
 * Useful when you want to allow events to fire again
 */
export function clearFiredEventsForPath(pathname: string): void {
  for (const key of globalEventState.firedEvents) {
    if (key.endsWith(`::${pathname}`)) {
      globalEventState.firedEvents.delete(key);
      globalEventState.eventTimestamps.delete(key);
    }
  }
}

/**
 * Resets the initial page events flag
 * Called when navigating to allow initial events to fire on the new page
 */
export function resetInitialPageEvents(): void {
  globalEventState.initialPageEventsFired = false;
}
